/**
 * Task: SearchBar — debounced wiki search input.
 */
import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Search, X, Loader2 } from 'lucide-react';

export default function SearchBar({ onSearch, searching, resultCount, query }) {
  const [value, setValue] = useState(query || '');
  const debounceRef = useRef(null);

  useEffect(() => {
    setValue(query || '');
  }, [query]);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(v), 250);
  };

  const handleClear = () => {
    setValue('');
    onSearch('');
  };

  return (
    <div className="relative" data-testid="confluence-search-bar">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {searching ? (
          <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
        ) : (
          <Search className="h-4 w-4 text-slate-400" />
        )}
      </div>
      <Input
        type="text"
        placeholder="Search the wiki — e.g. 'mechanic productivity', 'fixed expense', 'GST'…"
        value={value}
        onChange={handleChange}
        className="pl-10 pr-10 h-11 text-sm"
        data-testid="confluence-search-input"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          data-testid="confluence-search-clear"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {value && resultCount !== undefined && (
        <p className="text-xs text-slate-500 mt-1.5 ml-1" data-testid="confluence-search-result-count">
          {resultCount === 0
            ? `No matches for "${value}" — try a broader term.`
            : `${resultCount} match${resultCount === 1 ? '' : 'es'} for "${value}"`}
        </p>
      )}
    </div>
  );
}
